/**
 * useColdScenarios Hook
 *
 * Encapsulates scenario list, selection, CRUD, and save status for cold storage.
 * Follows the same pattern as useEVScenarios.
 *
 * All callbacks use functional updaters to avoid stale closures.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@vortx/shared-ui';
import * as coldScenarioService from '../services/coldScenarioService';

export function useColdScenarios(projectId, currentProject) {
    const { currentUser } = useAuth();

    const [scenarios, setScenarios] = useState([]);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [loading, setLoading] = useState(false);

    // Scenario save status
    const [saveStatus, setSaveStatus] = useState('idle');
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [saveError, setSaveError] = useState(null);

    // Track which project we auto-created for (prevent duplicate auto-creates)
    const autoCreatedForRef = useRef(null);

    // Load scenarios when project is loaded
    useEffect(() => {
        // Always clear stale data immediately when dependencies change
        setScenarios([]);
        setSelectedScenario(null);

        if (!projectId || !currentUser || !currentProject) {
            return;
        }

        let cancelled = false;

        const loadScenarios = async () => {
            try {
                setLoading(true);
                const data = await coldScenarioService.getColdScenarios(projectId, currentUser);
                if (cancelled) return;
                setScenarios(data);

                // Auto-create a "Default" scenario if project has none
                if (data.length === 0 && autoCreatedForRef.current !== projectId) {
                    autoCreatedForRef.current = projectId;
                    try {
                        const scenario = await coldScenarioService.createColdScenario(projectId, 'Default', currentUser);
                        if (cancelled) return;
                        setScenarios([scenario]);
                        setSelectedScenario(scenario);
                    } catch (createErr) {
                        console.error('Failed to auto-create default scenario:', createErr);
                    }
                }
            } catch (err) {
                console.error('Failed to load cold scenarios:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadScenarios();

        return () => { cancelled = true; };
    }, [projectId, currentUser, currentProject]);

    // CRUD
    const createScenario = useCallback(async (name = 'New Scenario') => {
        if (!currentUser || !projectId) return;
        try {
            const scenario = await coldScenarioService.createColdScenario(projectId, name, currentUser);
            setScenarios(prev => [scenario, ...prev]);
            setSelectedScenario(scenario);
            return scenario;
        } catch (err) {
            throw err;
        }
    }, [currentUser, projectId]);

    const deleteScenario = useCallback(async (scenarioId) => {
        if (!currentUser || !projectId) return;
        if (!window.confirm('Delete this scenario?')) return;
        try {
            await coldScenarioService.deleteColdScenario(projectId, scenarioId, currentUser);
            setScenarios(prev => prev.filter(s => s.id !== scenarioId));
            setSelectedScenario(prev => prev?.id === scenarioId ? null : prev);
        } catch (err) {
            throw err;
        }
    }, [currentUser, projectId]);

    const duplicateScenario = useCallback(async (scenarioId) => {
        if (!currentUser || !projectId) return;
        try {
            const scenario = await coldScenarioService.duplicateColdScenario(projectId, scenarioId, currentUser);
            setScenarios(prev => [scenario, ...prev]);
            return scenario;
        } catch (err) {
            throw err;
        }
    }, [currentUser, projectId]);

    // Selection — fetch full scenario from backend (includes GCS results)
    const selectScenario = useCallback(async (scenario) => {
        setSaveStatus('idle');
        setSaveError(null);

        // Set immediately with list data for fast UI response
        setSelectedScenario(scenario);

        // Then fetch full scenario (with GCS-hydrated results)
        if (currentUser && projectId && scenario?.id) {
            try {
                const full = await coldScenarioService.getColdScenario(
                    projectId,
                    scenario.id,
                    currentUser
                );
                // Guard against race: only set if this scenario is still selected
                setSelectedScenario(prev => prev?.id === full.id ? full : prev);
            } catch (err) {
                console.error('Failed to fetch full scenario:', err);
                // Keep list data as fallback
            }
        }
    }, [currentUser, projectId]);

    const deselectScenario = useCallback(() => {
        setSelectedScenario(null);
        setSaveStatus('idle');
        setSaveError(null);
    }, []);

    // In-memory update (optimistic, marks dirty)
    const updateScenario = useCallback((updates) => {
        setSelectedScenario(prev => {
            if (!prev) return prev;
            return { ...prev, ...updates };
        });
        setSaveStatus('dirty');
    }, []);

    // Explicit save to backend
    const saveScenario = useCallback(async () => {
        if (!currentUser || !projectId) return;

        // Read current selectedScenario via functional pattern
        let scenarioToSave = null;
        setSelectedScenario(prev => {
            scenarioToSave = prev;
            return prev;
        });

        if (!scenarioToSave) return;

        try {
            setSaveStatus('saving');
            setSaveError(null);
            const updated = await coldScenarioService.updateColdScenario(
                projectId,
                scenarioToSave.id,
                scenarioToSave,
                currentUser
            );
            setSelectedScenario(updated);
            setScenarios(prev => prev.map(s => s.id === updated.id ? updated : s));
            setSaveStatus('saved');
            setLastSavedAt(new Date().toISOString());
        } catch (err) {
            setSaveStatus('error');
            setSaveError(err.message);
        }
    }, [currentUser, projectId]);

    return {
        // State
        scenarios,
        selectedScenario,
        loading,
        saveStatus,
        lastSavedAt,
        saveError,

        // Expose setter for external updates (e.g., optimization results)
        setScenarios,

        // Actions
        createScenario,
        deleteScenario,
        duplicateScenario,

        // Selection
        selectScenario,
        deselectScenario,

        // Edit
        updateScenario,

        // Save
        saveScenario,
    };
}
